import { motion } from 'framer-motion'
import styled, { createGlobalStyle } from 'styled-components'
import { BrandLogo } from '@velasca/ui/brand-logo'
import { IconButton } from '@velasca/ui/icon-button'
import { ResponsiveImage } from '@velasca/ui/responsive-image'
import { UnstyledList } from '@velasca/ui/unstyled-list'
import CurrencySelect from '../CurrencySelect'

// Global styles for scroll lock compensation
export const ScrollLockGlobalStyles = createGlobalStyle`
  body.scroll-locked {
    padding-right: var(--scrollbar-width, 0);
  }
  
  body.scroll-locked [data-testid="desktop-navigation"] {
    padding-right: var(--scrollbar-width, 0);
  }
`

interface HeaderStatesProps {
  hover: boolean
  isOpen: boolean
  top: boolean
  sticky: boolean
  searchActivated: boolean
}

const MenuEntry = styled.div`
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;
  user-select: none;
`

export const FirstLevelMenuTitle = styled(MenuEntry)<{ isSelected: boolean }>`
  display: block;
  text-align: center;
  height: 100%;
  text-transform: uppercase;
  font-weight: ${({ isSelected }) => (isSelected ? '600' : '400')};
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.primary : theme.colors.text.primary};
  letter-spacing: 0rem;
  line-height: 1.25;
  font-size: 1.4rem;
  font-family: ${({ theme }) => theme.fonts.brand};
  padding: 1.3rem 1.4rem;

  &:hover {
    color: ${({ theme }) => theme.colors.primary} !important;
    cursor: pointer;
  }

  &.italic-blue {
    &:hover {
      color: ${({ theme }) => theme.colors.primaryLight}!important;
    }
  }

  &.boxed {
    &:hover {
      color: ${({ theme }) => theme.colors.text.military500}!important;
    }
  }

  &:after {
    display: block;
    content: attr(title);
    font-weight: 600;
    height: 1px;
    color: transparent;
    overflow: hidden;
    visibility: hidden;
  }
`

export const SecondLevelMenuTitle = styled(MenuEntry)`
  text-transform: uppercase;
  font-size: 1.4rem;
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: 0;

  & a {
    font-size: 1.4rem;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: 0;

    &.italic-blue {
      color: ${({ theme }) => theme.colors.primaryLight}!important;
    }

`

export const ThirdLevelMenuTitle = styled(MenuEntry)<{ viewAll?: boolean }>`
  font-size: 1.4rem;
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 400;
  line-height: 1.25;
  letter-spacing: 0;

  & a {
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 1.25;
    letter-spacing: 0;
    text-decoration: ${({ viewAll }) =>
      viewAll ? 'underline !important' : 'none'};

    &:hover {
      text-decoration: underline;
    }
  }
`

export const Header = styled.div<HeaderStatesProps>`
  z-index: 150;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;

  ${FirstLevelMenuTitle} {
    line-height: 1rem;
    color: ${({ theme, hover, isOpen, top, sticky, searchActivated }) =>
      top && !isOpen && !hover && !sticky && !searchActivated
        ? theme.colors.text.secondary
        : theme.colors.text.primary};
    &.italic-blue {
      font-style: italic;
      color: ${({ theme, hover, isOpen, top, sticky, searchActivated }) =>
        top && !isOpen && !hover && !sticky && !searchActivated
          ? theme.colors.text.secondary
          : theme.colors.primaryLight};
    }

    &.italic {
      font-style: italic;
    }

    &.boxed {
      font-style: italic;
      color: ${({ theme, hover, isOpen, top, sticky, searchActivated }) =>
        top && !isOpen && !hover && !sticky && !searchActivated
          ? theme.colors.text.secondary
          : theme.colors.text.military500};
      outline: 1px solid
        ${({ theme, hover, isOpen, top, sticky, searchActivated }) =>
          top && !isOpen && !hover && !sticky && !searchActivated
            ? theme.colors.text.secondary
            : theme.colors.text.military500};
      outline-offset: -5px;
    }
  }
`

export const Background = styled.div`
  position: relative;
`

export const MenuBackground = styled.div`
  position: relative;
`

export const MenuLinksBackground = styled(motion.div)<HeaderStatesProps>`
  position: absolute;
  right: 0;
  left: 0;
  background: ${({ theme, isOpen, hover, top, sticky, searchActivated }) =>
    (isOpen || hover || !top || sticky || searchActivated) &&
    theme.colors.background};
  overflow: hidden;
`

export const Navbar = styled.nav<HeaderStatesProps>`
  background: ${({ theme, isOpen, hover, top, sticky, searchActivated }) =>
    isOpen || hover || !top || sticky || searchActivated
      ? theme.colors.background
      : 'transparent'};
  transition: background 0.1s linear;
  line-height: 1.42857143;
`

export const NavHead = styled.div`
  display: flex;
  max-width: 1600px;
  margin: 0 auto;
  padding: 0.5rem 0;
`

export const StyledBrandLogoExtended = styled(BrandLogo.Extended)`
  width: 14rem;
  height: auto;
  transition: opacity 0.1s cubic-bezier(0, 0, 0, 1) 0s;
`

export const StyledBrandLogoShort = styled(BrandLogo.Short)<{
  $alwaysShow: boolean
}>`
  opacity: ${({ $alwaysShow }) => ($alwaysShow ? 1 : 0)};
  transition: opacity 0.1s cubic-bezier(0, 0, 0, 1) 0s;
  width: 4.75rem;
  height: auto;
`

export const Separator = styled.div`
  width: 0.5px;
  margin: 0 0.75rem;
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;
  transform: rotate(10deg);
`
export const SpaceSeparator = styled.div`
  width: 0.5px;
  margin: 0.75rem;
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;
`

export const Link = styled.a`
  &:hover,
  &:link,
  &:visited,
  &:active {
    text-decoration: none;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
`

export const NavLink = styled(Link)`
  display: block;
  font-size: 1.2rem;
  line-height: 1.25;
  letter-spacing: 0rem;

  color: ${({ theme }) => theme.colors.text.primary};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  ${FirstLevelMenuTitle} {
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
      font-weight: 600;
    }
  }
`

export const HelpLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 400;
  font-style: italic;
  font-size: 1.2rem;
  line-height: 1.25;
  letter-spacing: 0rem;
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`

export const HelpLink = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-weight: 400;
  font-style: italic;
  font-size: 1.2rem;
  line-height: 1.25;
  letter-spacing: 0rem;
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;

  & strong {
    font-weight: 600;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: underline;
    cursor: pointer;
  }
`

export const Select = styled(CurrencySelect)`
  font-family: ${({ theme }) => theme.fonts.brand};
  font-size: 1.2rem !important;
  font-weight: 600;
  width: auto;
  border: none;
  background: none !important;
  padding: 0 1.75rem 0 0.5rem;
  margin-bottom: 0;
  line-height: 1.25;
  letter-spacing: 0rem;
  font-style: italic;
  transition: color 0.2s cubic-bezier(0, 0, 0, 1) 0s;

  &:hover {
    text-decoration: underline;
  }
`

export const HelpMenu = styled.div<HeaderStatesProps>`
  background: ${({ theme, isOpen, hover, top, sticky, searchActivated }) =>
    isOpen || hover || !top || sticky || searchActivated
      ? theme.colors.background
      : `transparent`};
  transition: background 0.1s linear;
  ${HelpLabel} {
    color: ${({ isOpen, theme, hover, sticky, top, searchActivated }) =>
      isOpen || hover || sticky || !top || searchActivated
        ? theme.colors.text.light
        : theme.colors.text.secondary};
  }
  ${HelpLink} {
    color: ${({ isOpen, theme, hover, sticky, top, searchActivated }) =>
      isOpen || hover || sticky || !top || searchActivated
        ? theme.colors.text.light
        : theme.colors.text.secondary};
  }

  ${Select} {
    color: ${({ isOpen, theme, hover, sticky, top, searchActivated }) =>
      isOpen || hover || sticky || !top || searchActivated
        ? theme.colors.text.light
        : theme.colors.text.secondary}!important;
  }

  ${Separator} {
    background: ${({ isOpen, theme, hover, sticky, top, searchActivated }) =>
      isOpen || hover || sticky || !top || searchActivated
        ? theme.colors.text.light
        : theme.colors.text.secondary};
  }
`

export const HelpMenuContent = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  position: relative;
  height: 90px;
`

const HelpChunk = styled.div`
  height: fit-content;
`

export const StartHelpChunk = styled(HelpChunk)`
  display: flex;
  justify-content: flex-start;
  padding-top: 2rem;
  padding-left: 1.5rem;
  position: absolute;
  top: 0;
  left: 0;
`

export const CenterHelpChunk = styled(HelpChunk)`
  padding-top: 2.8rem;
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
`

export const EndHelpChunk = styled(HelpChunk)`
  display: flex;
  justify-content: flex-end;
  padding-top: 2rem;
  padding-right: 1.5rem;
  position: absolute;
  top: 0;
  right: 0;
`

const Chunk = styled.div`
  display: flex;
  align-items: center;
`

export const StartChunk = styled(Chunk)`
  flex: 1;
  padding-right: 3rem;
  padding-left: 1rem; // should be 1.5rem, but icon has margin
`

export const CenterChunk = styled(UnstyledList)`
  display: flex;
  align-items: center;
`

export const EndChunk = styled(Chunk)`
  padding-left: 3rem;
  padding-right: 0.25rem; // again, should be 1.5rem, but icon has margin
  justify-content: flex-end;
  flex: 1;
`

export const MenuLinks = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  border-top: 1px solid ${({ theme }) => theme.colors.secondary};
`

export const CenterMenuLinksContainer = styled.div`
  padding: 3rem 3.3rem;
  display: flex;
  width: 100%;
  max-width: 1600px;
  gap: 4rem;
`

const Column = styled.div``

export const MenuColumn = styled(Column)`
  display: flex;
  flex-direction: column;
  width: 16rem;
  gap: 3rem;
`

export const MediaColumn = styled(Column)`
  display: flex;
  flex-grow: 1;

  .conditional-link-void {
    display: flex;
    flex-direction: column;
  }
`
export const SecondLevelGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

export const SecondLevelMenuChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

export const ImagesWrapper = styled.div<{ contentLoaded: boolean }>`
  display: flex;
  flex-grow: 1;
  justify-content: end;
  gap: 0.5rem;
  opacity: ${({ contentLoaded }) => (contentLoaded ? 1 : 0.8)};
  transition: opacity 0.3s ease-in-out;
`

export const ImageContainer = styled.div`
  --aspect-ratio: 4 / 5;
  --image-height: 65vh;
  --image-height-lg: 45vh;

  flex-shrink: 0;
  aspect-ratio: var(--aspect-ratio);
  height: var(--image-height);
  width: calc(var(--image-height) * var(--aspect-ratio));

  @media (min-width: 1920px) {
    height: var(--image-height-lg);
    width: calc(var(--image-height-lg) * var(--aspect-ratio));
  }
`

export const Image = styled(ResponsiveImage)`
  height: 100%;
  width: 100%;
  object-fit: cover;
`

export const ImageLink = styled(Link)`
  line-height: 2rem;
  display: flex;
  flex-direction: column;
`

export const EmptyImage = styled.div`
  aspect-ratio: var(--aspect-ratio);
  width: calc(var(--image-height) * var(--aspect-ratio));
  height: var(--image-height);
  background: ${({ theme }) => theme.colors.boxBackground};

  @media (min-width: 1920px) {
    height: var(--image-height-lg);
    width: calc(var(--image-height-lg) * var(--aspect-ratio));
  }
`

export const ImageTitle = styled.span<{ visible?: boolean }>`
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: ${({ theme }) => theme.fonts.brand};
  font-size: 1.4rem;
  font-weight: 500;
  line-height: 1.25;
  letter-spacing: 0;
  text-decoration: underline;
  text-transform: uppercase;
  display: block;
  margin-top: 10px;
  opacity: ${({ visible = true }) => (visible ? 1 : 0)};
`

export const CurrencySelector = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

export const AccessibilityButton = styled(IconButton)`
  user-select: none;
`

export const ImageWithSkeletonContainer = styled.div`
  position: relative;
  aspect-ratio: var(--aspect-ratio);
  width: calc(var(--image-height) * var(--aspect-ratio));
  height: var(--image-height);

  @media (min-width: 1920px) {
    height: var(--image-height-lg);
    width: calc(var(--image-height-lg) * var(--aspect-ratio));
  }
`
